import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services'
import { TransformedReadingsResponse } from './useDeviceReadings'

/**
 * Mutation hook for hiding sensor readings
 *
 * Hides readings in a specific time range for a device.
 * Uses setQueriesData for instant client-side updates without flickering.
 *
 * @returns Mutation hook for hiding readings
 */
export function useHideReadings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      sigfoxId,
      from,
      to,
      sensorPort,
    }: {
      sigfoxId: string
      from: number
      to: number
      sensorPort?: string
    }) => {
      return await api.hideReadings(sigfoxId, from, to, sensorPort)
    },
    onSuccess: (_, { sigfoxId, from, to, sensorPort }) => {
      // Update all cached readings queries for this device
      queryClient.setQueriesData<TransformedReadingsResponse>(
        { queryKey: ['readings', sigfoxId] },
        (oldData) => {
          if (!oldData) return oldData
          return markReadingsHidden(oldData, from, to, sensorPort, true)
        }
      )
    },
  })
}

/**
 * Mutation hook for unhiding sensor readings
 *
 * Unhides previously hidden readings for a device.
 * Uses setQueriesData for instant client-side updates without flickering.
 *
 * @returns Mutation hook for unhiding readings
 */
export function useUnhideReadings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      sigfoxId,
      from,
      to,
      sensorPort,
    }: {
      sigfoxId: string
      from?: number
      to?: number
      sensorPort?: string
    }) => {
      return await api.unhideReadings(sigfoxId, from, to, sensorPort)
    },
    onSuccess: (_, { sigfoxId, from, to, sensorPort }) => {
      // Update all cached readings queries for this device
      queryClient.setQueriesData<TransformedReadingsResponse>(
        { queryKey: ['readings', sigfoxId] },
        (oldData) => {
          if (!oldData) return oldData
          return markReadingsHidden(oldData, from, to, sensorPort, false)
        }
      )
    },
  })
}

/**
 * Helper: update hidden map for readings in time range
 */
function markReadingsHidden(
  data: TransformedReadingsResponse,
  from: number | undefined,
  to: number | undefined,
  sensorPort: string | undefined,
  hidden: boolean
): TransformedReadingsResponse {
  const updatedSensors = { ...data.sensors }

  for (const [sensorKey, sensorData] of Object.entries(updatedSensors)) {
    // Skip if sensorPort specified and doesn't match
    if (sensorPort && !sensorKey.startsWith(sensorPort)) continue

    const updatedHidden = { ...(sensorData.hidden || {}) }

    // Update hidden status for timestamps in range
    Object.keys(sensorData.readings || {}).forEach(ts => {
      const timestamp = parseInt(ts)
      // If from/to not specified (restore all), update all timestamps
      const inRange = (from === undefined || to === undefined)
        ? true
        : (timestamp >= from && timestamp <= to)

      if (inRange) {
        if (hidden) {
          updatedHidden[ts] = true
        } else {
          delete updatedHidden[ts]
        }
      }
    })

    updatedSensors[sensorKey] = {
      ...sensorData,
      hidden: updatedHidden,
    }
  }

  return { ...data, sensors: updatedSensors }
}
